import React, { useEffect, useRef, useState } from 'react';
import { Canvas, FabricImage, Textbox, Control, util } from 'fabric';
import { Button } from './ui/button';
import { Upload, Type, Save, Trash2, Bold, Italic } from 'lucide-react';

export default function CardEditor({ cardData, onSave, initialStates }) {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const [activeObject, setActiveObject] = useState(null);
  const [selectedSide, setSelectedSide] = useState('front'); // 'front' or 'back'
  const [selectedColor, setSelectedColor] = useState(null); // Selected color variant
  
  // Store canvas state for each side - using useRef for synchronous access
  const canvasStatesRef = useRef({
    front: null,
    back: null
  });
  const [canvasStates, setCanvasStates] = useState({
    front: null,
    back: null
  });
  
  // Text styling states
  const [fontSize, setFontSize] = useState(20);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [textColor, setTextColor] = useState('#000000');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [textAlign, setTextAlign] = useState('left');

  // Signature states
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureText, setSignatureText] = useState('');
  const [signatureColor, setSignatureColor] = useState('#000000');
  const [signatureStyleIndex, setSignatureStyleIndex] = useState(0);

  // Different signature styles - 15 diverse options
  const signatureStyles = [
    { name: 'Brush Script', fontFamily: 'Brush Script MT, cursive', fontStyle: 'italic', weight: 'normal' },
    { name: 'Dancing Script', fontFamily: 'Dancing Script, cursive', fontStyle: 'normal', weight: '700' },
    { name: 'Pacifico', fontFamily: 'Pacifico, cursive', fontStyle: 'normal', weight: 'normal' },
    { name: 'Great Vibes', fontFamily: 'Great Vibes, cursive', fontStyle: 'normal', weight: 'normal' },
    { name: 'Allura', fontFamily: 'Allura, cursive', fontStyle: 'normal', weight: 'normal' },
    { name: 'Sacramento', fontFamily: 'Sacramento, cursive', fontStyle: 'normal', weight: 'normal' },
    { name: 'Satisfy', fontFamily: 'Satisfy, cursive', fontStyle: 'normal', weight: 'normal' },
    { name: 'Yellowtail', fontFamily: 'Yellowtail, cursive', fontStyle: 'normal', weight: 'normal' },
    { name: 'Cookie', fontFamily: 'Cookie, cursive', fontStyle: 'normal', weight: 'normal' },
    { name: 'Alex Brush', fontFamily: 'Alex Brush, cursive', fontStyle: 'normal', weight: 'normal' },
    { name: 'Tangerine', fontFamily: 'Tangerine, cursive', fontStyle: 'normal', weight: '700' },
    { name: 'Kaushan Script', fontFamily: 'Kaushan Script, cursive', fontStyle: 'normal', weight: 'normal' },
    { name: 'Courgette', fontFamily: 'Courgette, cursive', fontStyle: 'normal', weight: 'normal' },
    { name: 'Indie Flower', fontFamily: 'Indie Flower, cursive', fontStyle: 'normal', weight: 'normal' },
    { name: 'Amatic SC', fontFamily: 'Amatic SC, cursive', fontStyle: 'normal', weight: '700' },
  ];

  // Restore canvas states when initialStates prop changes
  useEffect(() => {
    if (initialStates) {
      setCanvasStates(initialStates);
      canvasStatesRef.current = initialStates;
    }
  }, [initialStates]);

  // Initialize selected color from color_variants
  useEffect(() => {
    if (cardData.color_variants && cardData.color_variants.length > 0 && !selectedColor) {
      const firstVariant = cardData.color_variants[0];
      setSelectedColor({
        id: firstVariant.colour_id,
        name: firstVariant.colour_name,
        code: firstVariant.colour_code
      });
    }
  }, [cardData, selectedColor]);

  useEffect(() => {
    if (!canvasRef.current || !selectedColor) return;

    // Initialize Fabric canvas
    const canvas = new Canvas(canvasRef.current, {
      width: 600,
      height: 375, // Standard business card ratio (3.5" x 2" scaled)
      backgroundColor: '#f0f0f0',
    });

    // Add delete control to objects
    canvas.on('object:added', (e) => {
      if (e.target) {
        e.target.set({
          borderColor: '#4F46E5',
          cornerColor: '#4F46E5',
          cornerSize: 10,
          transparentCorners: false,
        });
        
        // Add delete icon control
        const deleteIcon = "data:image/svg+xml,%3C%3Fxml version='1.0' encoding='utf-8'%3F%3E%3C!DOCTYPE svg PUBLIC '-//W3C//DTD SVG 1.1//EN' 'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3E%3Csvg version='1.1' id='Ebene_1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' width='595.275px' height='595.275px' viewBox='200 215 230 470' xml:space='preserve'%3E%3Ccircle style='fill:%23F44336;' cx='299.76' cy='439.067' r='218.516'/%3E%3Cg%3E%3Crect x='267.162' y='307.978' transform='matrix(0.7071 -0.7071 0.7071 0.7071 -222.6202 340.6915)' style='fill:white;' width='65.545' height='262.18'/%3E%3Crect x='266.988' y='308.153' transform='matrix(0.7071 0.7071 -0.7071 0.7071 398.3889 -83.3116)' style='fill:white;' width='65.544' height='262.179'/%3E%3C/g%3E%3C/svg%3E";
        
        const deleteImg = document.createElement('img');
        deleteImg.src = deleteIcon;
        
        const deleteControl = new Control({
          x: 0.5,
          y: -0.5,
          offsetY: -16,
          offsetX: 16,
          cursorStyle: 'pointer',
          mouseUpHandler: function(eventData, transform) {
            const target = transform.target;
            const canvas = target.canvas;
            
            // Show confirmation dialog
            if (window.confirm('Are you sure you want to delete this element?')) {
              canvas.remove(target);
              canvas.requestRenderAll();
            }
          },
          render: function(ctx, left, top, styleOverride, fabricObject) {
            const size = 24;
            ctx.save();
            ctx.translate(left, top);
            ctx.rotate(util.degreesToRadians(fabricObject.angle));
            ctx.drawImage(deleteImg, -size/2, -size/2, size, size);
            ctx.restore();
          },
        });
        
        if (e.target.type !== 'activeSelection') {
          e.target.controls.deleteControl = deleteControl;
        }
      }
    });

    fabricCanvasRef.current = canvas;

    // Load card background image based on selected color and side
    let imageUrl = null;
    
    // Find the variant for the selected color
    const selectedVariant = cardData.color_variants?.find(v => v.colour_id === selectedColor.id);
    
    if (selectedVariant) {
      imageUrl = selectedSide === 'front' 
        ? `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${selectedVariant.front_image}`
        : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${selectedVariant.back_image}`;
    }

    if (imageUrl) {
      console.log(`Loading ${selectedSide} side, image URL:`, imageUrl);
      FabricImage.fromURL(imageUrl, { crossOrigin: 'anonymous' }).then((img) => {
        console.log(`Background image loaded for ${selectedSide} side`);
        img.scaleToWidth(600);
        img.scaleToHeight(375);
        img.selectable = false;
        img.evented = false;
        canvas.backgroundImage = img;
        
        // Restore canvas objects for this side using the ref for synchronous access
        const savedState = canvasStatesRef.current[selectedSide];
        console.log(`Saved state for ${selectedSide}:`, savedState ? `${savedState.objects?.length || 0} objects` : 'null');
        
        if (savedState && savedState.objects && savedState.objects.length > 0) {
          // Clear existing objects (but not background)
          const objects = canvas.getObjects();
          objects.forEach(obj => canvas.remove(obj));
          
          // Manually restore objects from saved state instead of using loadFromJSON
          // This gives us better control and avoids background image issues
          util.enlivenObjects(savedState.objects).then((enlivenedObjects) => {
            console.log(`Restored ${enlivenedObjects.length} objects to ${selectedSide} side`);
            enlivenedObjects.forEach((obj) => {
              canvas.add(obj);
            });
            // Ensure background is still set after adding objects
            canvas.backgroundImage = img;
            canvas.renderAll();
            console.log(`Canvas rendered for ${selectedSide} side with ${enlivenedObjects.length} objects`);
          });
        } else {
          // No saved objects, just render the background
          canvas.renderAll();
          console.log(`Canvas rendered for ${selectedSide} side (no saved objects)`);
        }
      }).catch(error => {
        console.error(`Failed to load background image for ${selectedSide}:`, error);
      });
    }

    // Handle object selection
    canvas.on('selection:created', (e) => {
      const selected = e.selected[0];
      setActiveObject(selected);
      if (selected && selected.type === 'textbox') {
        updateTextControls(selected);
      }
    });

    canvas.on('selection:updated', (e) => {
      const selected = e.selected[0];
      setActiveObject(selected);
      if (selected && selected.type === 'textbox') {
        updateTextControls(selected);
      }
    });

    canvas.on('selection:cleared', () => {
      setActiveObject(null);
    });

    return () => {
      canvas.dispose();
    };
  }, [cardData, selectedSide, selectedColor]);

  const updateTextControls = (textObj) => {
    setFontSize(textObj.fontSize || 20);
    setFontFamily(textObj.fontFamily || 'Arial');
    setTextColor(textObj.fill || '#000000');
    setIsBold(textObj.fontWeight === 'bold');
    setIsItalic(textObj.fontStyle === 'italic');
    setTextAlign(textObj.textAlign || 'left');
  };

  const addText = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const text = new Textbox('Edit Me', {
      left: 50,
      top: 50,
      fontSize: 20,
      fill: '#000000',
      fontFamily: 'Arial',
      width: 200,
    });

    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
  };

  const addImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        FabricImage.fromURL(event.target.result).then((img) => {
          img.scaleToWidth(150);
          img.set({
            left: 100,
            top: 100,
          });
          fabricCanvasRef.current.add(img);
          fabricCanvasRef.current.setActiveObject(img);
          fabricCanvasRef.current.renderAll();
        });
      };
      reader.readAsDataURL(file);
    };
    
    input.click();
  };

  const deleteSelected = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !activeObject) return;

    // Show confirmation dialog
    if (window.confirm('Are you sure you want to delete this element?')) {
      canvas.remove(activeObject);
      canvas.renderAll();
      setActiveObject(null);
    }
  };

  const updateTextProperty = (property, value) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !activeObject || activeObject.type !== 'textbox') return;

    activeObject.set(property, value);
    canvas.renderAll();
  };

  const handleFontSizeChange = (e) => {
    const size = parseInt(e.target.value);
    setFontSize(size);
    updateTextProperty('fontSize', size);
  };

  const handleFontFamilyChange = (e) => {
    const family = e.target.value;
    setFontFamily(family);
    updateTextProperty('fontFamily', family);
  };

  const handleColorChange = (e) => {
    const color = e.target.value;
    setTextColor(color);
    updateTextProperty('fill', color);
  };

  const toggleBold = () => {
    const newBold = !isBold;
    setIsBold(newBold);
    updateTextProperty('fontWeight', newBold ? 'bold' : 'normal');
  };

  const toggleItalic = () => {
    const newItalic = !isItalic;
    setIsItalic(newItalic);
    updateTextProperty('fontStyle', newItalic ? 'italic' : 'normal');
  };

  const handleAlignmentChange = (alignment) => {
    setTextAlign(alignment);
    updateTextProperty('textAlign', alignment);
  };

  const regenerateSignatureStyle = () => {
    setSignatureStyleIndex((prev) => (prev + 1) % signatureStyles.length);
  };

  const addSignature = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !signatureText.trim()) return;

    const currentStyle = signatureStyles[signatureStyleIndex];

    // Create signature-style text
    const signature = new Textbox(signatureText, {
      left: 100,
      top: 250,
      fontSize: 32,
      fill: signatureColor,
      fontFamily: currentStyle.fontFamily,
      fontStyle: currentStyle.fontStyle,
      fontWeight: currentStyle.weight,
      width: 300,
    });

    canvas.add(signature);
    canvas.setActiveObject(signature);
    canvas.renderAll();
    
    // Close modal
    setShowSignatureModal(false);
    setSignatureText('');
  };

  const handleSave = async () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Save current canvas state (excluding background to save space)
    const currentState = canvas.toJSON(['selectable', 'evented']);
    const updatedStates = {
      ...canvasStatesRef.current,
      [selectedSide]: currentState
    };
    // Update both ref and state
    canvasStatesRef.current = updatedStates;
    setCanvasStates(updatedStates);

    // Get JSON data for current side
    const jsonData = currentState;

    // Generate final image for current side
    const imageDataURL = canvas.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: 2, // Higher resolution for printing
    });

    // Convert data URL to blob
    const blob = await fetch(imageDataURL).then(r => r.blob());

    // Get price for selected color
    const selectedVariant = cardData.color_variants?.find(v => v.colour_id === selectedColor?.id);
    const price = selectedVariant?.price || 0;

    // Generate preview for other side if it has content
    let otherSideData = null;
    const otherSide = selectedSide === 'front' ? 'back' : 'front';
    if (updatedStates[otherSide]) {
      // Find the image URL for the other side
      const otherSideImageUrl = otherSide === 'front'
        ? `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${cardData.color_variants?.find(v => v.colour_id === selectedColor?.id)?.front_image}`
        : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${cardData.color_variants?.find(v => v.colour_id === selectedColor?.id)?.back_image}`;

      // Create temporary canvas to generate preview
      const tempCanvas = new Canvas(document.createElement('canvas'), {
        width: 600,
        height: 375,
      });
      
      // Load background image for other side
      if (otherSideImageUrl) {
        const bgImg = await FabricImage.fromURL(otherSideImageUrl, { crossOrigin: 'anonymous' });
        bgImg.scaleToWidth(600);
        bgImg.scaleToHeight(375);
        bgImg.selectable = false;
        bgImg.evented = false;
        tempCanvas.backgroundImage = bgImg;
      }
      
      // Load saved objects
      await tempCanvas.loadFromJSON(updatedStates[otherSide], () => {
        // Restore background after loadFromJSON
        if (otherSideImageUrl) {
          FabricImage.fromURL(otherSideImageUrl, { crossOrigin: 'anonymous' }).then((bgImg) => {
            bgImg.scaleToWidth(600);
            bgImg.scaleToHeight(375);
            bgImg.selectable = false;
            bgImg.evented = false;
            tempCanvas.backgroundImage = bgImg;
            tempCanvas.renderAll();
          });
        }
      });
      
      // Small delay to ensure rendering is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const otherImageDataURL = tempCanvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: 2,
      });
      
      otherSideData = {
        jsonData: updatedStates[otherSide],
        imageDataURL: otherImageDataURL
      };
      
      tempCanvas.dispose();
    }

    onSave({
      side: selectedSide,
      jsonData: jsonData,
      imageBlob: blob,
      imageDataURL: imageDataURL,
      selectedColor: selectedColor,
      price: price,
      otherSide: otherSide,
      otherSideData: otherSideData,
      allStates: updatedStates
    });
  };

  const switchSide = (side) => {
    // Save current canvas state before switching - use ref for synchronous update
    const canvas = fabricCanvasRef.current;
    if (canvas) {
      // Save only the objects, not the background (we load background from URLs)
      const currentState = canvas.toJSON(['selectable', 'evented']);
      console.log(`Saving ${selectedSide} side state, objects:`, currentState.objects?.length || 0);
      
      // Update both ref (synchronous) and state (async)
      const updatedStates = {
        ...canvasStatesRef.current,
        [selectedSide]: currentState
      };
      canvasStatesRef.current = updatedStates;
      setCanvasStates(updatedStates);
    }
    
    console.log(`Switching from ${selectedSide} to ${side}`);
    setSelectedSide(side);
    setActiveObject(null);
  };

  return (
    <div className="flex gap-6">
      {/* Left Sidebar - Tools */}
      <div className="w-64 bg-white rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="text-lg font-bold text-navy mb-4">Tools</h3>
        
        {/* Add Text Button */}
        <Button
          onClick={addText}
          className="w-full bg-navy text-white hover:bg-navy/90 justify-start"
        >
          <Type className="w-4 h-4 mr-2" />
          Add Text
        </Button>

        {/* Add Image Button */}
        <Button
          onClick={addImage}
          className="w-full bg-navy text-white hover:bg-navy/90 justify-start"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Image
        </Button>

        {/* Add Signature Button */}
        <Button
          onClick={() => setShowSignatureModal(true)}
          className="w-full bg-purple-600 text-white hover:bg-purple-700 justify-start"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          Add Signature
        </Button>

        {/* Delete Button */}
        {activeObject && (
          <Button
            onClick={deleteSelected}
            variant="destructive"
            className="w-full justify-start"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Selected
          </Button>
        )}

        {/* Text Styling Controls */}
        {activeObject && activeObject.type === 'textbox' && (
          <div className="pt-4 border-t border-gray-200 space-y-3">
            <h4 className="font-semibold text-sm text-navy">Text Properties</h4>
            
            {/* Font Family */}
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">Font</label>
              <select
                value={fontFamily}
                onChange={handleFontFamilyChange}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
              >
                <option value="Arial">Arial</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Courier New">Courier New</option>
                <option value="Georgia">Georgia</option>
                <option value="Verdana">Verdana</option>
                <option value="Helvetica">Helvetica</option>
              </select>
            </div>

            {/* Font Size */}
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">
                Size: {fontSize}px
              </label>
              <input
                type="range"
                min="10"
                max="72"
                value={fontSize}
                onChange={handleFontSizeChange}
                className="w-full"
              />
            </div>

            {/* Color */}
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={textColor}
                  onChange={handleColorChange}
                  className="w-16 h-10 rounded-lg border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={textColor}
                  onChange={handleColorChange}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-300 font-mono text-sm uppercase"
                  placeholder="#000000"
                  maxLength="7"
                />
              </div>
            </div>

            {/* Bold and Italic */}
            <div className="flex gap-2">
              <Button
                onClick={toggleBold}
                variant={isBold ? "default" : "outline"}
                className="flex-1"
              >
                <Bold className="w-4 h-4" />
              </Button>
              <Button
                onClick={toggleItalic}
                variant={isItalic ? "default" : "outline"}
                className="flex-1"
              >
                <Italic className="w-4 h-4" />
              </Button>
            </div>

            {/* Text Alignment */}
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">Alignment</label>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleAlignmentChange('left')}
                  variant={textAlign === 'left' ? "default" : "outline"}
                  className="flex-1"
                  title="Align Left"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h16" />
                  </svg>
                </Button>
                <Button
                  onClick={() => handleAlignmentChange('center')}
                  variant={textAlign === 'center' ? "default" : "outline"}
                  className="flex-1"
                  title="Align Center"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M4 18h16" />
                  </svg>
                </Button>
                <Button
                  onClick={() => handleAlignmentChange('right')}
                  variant={textAlign === 'right' ? "default" : "outline"}
                  className="flex-1"
                  title="Align Right"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M10 12h10M4 18h16" />
                  </svg>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Card Side Toggle */}
        <div className="pt-4 border-t border-gray-200">
          <h4 className="font-semibold text-sm text-navy mb-2">Card Side</h4>
          <div className="flex gap-2">
            <Button
              onClick={() => switchSide('front')}
              variant={selectedSide === 'front' ? "default" : "outline"}
              className="flex-1"
            >
              Front
            </Button>
            <Button
              onClick={() => switchSide('back')}
              variant={selectedSide === 'back' ? "default" : "outline"}
              className="flex-1"
            >
              Back
            </Button>
          </div>
        </div>

        {/* Color Variant Selector */}
        {cardData.color_variants && cardData.color_variants.length > 1 && (
          <div className="pt-4 border-t border-gray-200">
            <h4 className="font-semibold text-sm text-navy mb-2">Card Color</h4>
            <div className="space-y-2">
              {cardData.color_variants.map((variant) => (
                <button
                  key={variant.colour_id}
                  onClick={() => setSelectedColor({
                    id: variant.colour_id,
                    name: variant.colour_name,
                    code: variant.colour_code
                  })}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                    selectedColor?.id === variant.colour_id
                      ? 'border-navy bg-navy/5'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div
                    className="w-6 h-6 rounded-full border border-gray-300"
                    style={{ backgroundColor: variant.colour_code }}
                  ></div>
                  <span className="text-sm font-medium">{variant.colour_name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Canvas Area */}
      <div className="flex-1">
        <div className="bg-white rounded-2xl p-8 shadow-sm">
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-xl font-bold text-navy">
              {selectedSide === 'front' ? 'Front' : 'Back'} Side
            </h3>
            <Button
              onClick={handleSave}
              className="bg-navy text-white hover:bg-navy/90"
            >
              <Save className="w-4 h-4 mr-2" />
              Save & Continue
            </Button>
          </div>
          
          <div className="border-2 border-gray-200 rounded-xl overflow-hidden inline-block">
            <canvas ref={canvasRef} />
          </div>

          <p className="text-sm text-gray-600 mt-4">
            Click "Add Text" or "Upload Image" to customize your card. Drag elements to reposition, use corners to resize.
          </p>
        </div>
      </div>

      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-navy mb-4">Add Signature</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  value={signatureText}
                  onChange={(e) => setSignatureText(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="John Doe"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Signature Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={signatureColor}
                    onChange={(e) => setSignatureColor(e.target.value)}
                    className="w-16 h-12 rounded-lg border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={signatureColor}
                    onChange={(e) => setSignatureColor(e.target.value)}
                    className="flex-1 px-4 py-3 rounded-lg border border-gray-300 font-mono uppercase"
                    placeholder="#000000"
                    maxLength="7"
                  />
                </div>
              </div>

              {/* Preview */}
              {signatureText && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-end mb-2">
                    <Button
                      onClick={regenerateSignatureStyle}
                      variant="outline"
                      size="sm"
                      className="text-xs py-1 px-3"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Try Another Style
                    </Button>
                  </div>
                  <div 
                    style={{ 
                      fontFamily: signatureStyles[signatureStyleIndex].fontFamily,
                      fontStyle: signatureStyles[signatureStyleIndex].fontStyle,
                      fontWeight: signatureStyles[signatureStyleIndex].weight,
                      fontSize: '32px',
                      color: signatureColor
                    }}
                  >
                    {signatureText}
                  </div>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => {
                    setShowSignatureModal(false);
                    setSignatureText('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={addSignature}
                  className="flex-1 bg-navy text-white hover:bg-navy/90"
                  disabled={!signatureText.trim()}
                >
                  Add to Card
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
